import React, { useState, useEffect, useRef, Fragment } from 'react';
import styles from './Carousel.module.scss';
import { useMediaQuery } from 'react-responsive';

const leftArrow = '/icons/carousel/left-arrow-head(1).svg';
const rightArrow = '/icons/carousel/left-arrow-head.svg';
const arrowRight = '/assets/svg/Arrows_Right.svg';
const arrowLeft = '/assets/svg/Arrows_Left.svg';
const carouselPause = '/assets/svg/pauseVideo.svg';
const carouselPlay = '/assets/svg/playVideo.svg';
const pauseSvg = '/assets/svg/pause-a.svg';
const playSvg = '/assets/svg/play-a.svg';

const Carousel = ({
  components = [],
  dots,
  hasArrows = false,
  style,
  startUpIndex,
  currentSlide,
  hasDots = true,
  className,
  slideClassName,
  nextStyle,
  prevStyle,
  StopSliderOnHover = false,
  containerClass,
  activeDotClass,
  getSlide,
  reset,
  isReseted,
  isArrowed = true,
  videoIndexes = [],
  isBlackArrow,
  animation = 'slide',
  fadeDuration = 500,
  fadeEasing = 'ease-in-out',
  showAutoPlayControl = false,
  autoPlayDefault = false,
  autoPlayInterval = 7000,
  maxDots = 10,
  onPauseClick,
  pauseCarrousel,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 700 });

  const [currentIndex, setCurrentIndex] = useState(startUpIndex ?? 0);
  const [startX, setStartX] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(autoPlayDefault);

  const [progress, setProgress] = useState(0);
  const [tickStart, setTickStart] = useState(null);

  const pausedElapsedRef = useRef(0);
  const prevIndexRef = useRef(startUpIndex ?? 0);

  const isFade = animation === 'fade';

  const maxVisibleDots = maxDots;
  const startDot = Math.max(
    Math.min(
      currentIndex - Math.floor(maxVisibleDots / 2),
      components.length - maxVisibleDots
    ),
    0
  );
  const endDot = Math.min(startDot + maxVisibleDots, components.length);

  const handleTouchStart = (e) => {
    if (!StopSliderOnHover) setStartX(e.touches[0].clientX);
  };
  const handleTouchEnd = (e) => {
    if (StopSliderOnHover) return;
    const endX = e.changedTouches[0].clientX;
    const swipeLength = startX - endX;
    if (swipeLength > 50) nextSlide();
    else if (swipeLength < -50) prevSlide();
  };

  const bumpTimer = () => {
    pausedElapsedRef.current = 0;
    setTickStart(performance.now());
    setProgress(0);
  };

  const nextSlide = () => {
    setCurrentIndex((p) => (p + 1) % components.length);
    if (isAutoPlay) bumpTimer();
  };

  const prevSlide = () => {
    setCurrentIndex((p) => (p - 1 + components.length) % components.length);
    if (isAutoPlay) bumpTimer();
  };

  useEffect(() => {
    if (currentSlide !== undefined && currentSlide !== currentIndex) {
      setCurrentIndex(currentSlide);
      if (isAutoPlay) bumpTimer();
    }
  }, [currentSlide]);

  useEffect(() => {
    getSlide?.(currentIndex);
  }, [currentIndex, getSlide]);

  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === 'ArrowLeft') prevSlide();
      else if (event.key === 'ArrowRight') nextSlide();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [currentIndex, components.length, isAutoPlay]);

  useEffect(() => {
    if (isReseted) setCurrentIndex(0);
  }, [reset, isReseted]);

  const isVideoIndex = (i) => {
    if (typeof videoIndexes === 'function') return !!videoIndexes(i);
    if (typeof videoIndexes === 'number') return i === videoIndexes;
    if (Array.isArray(videoIndexes)) return videoIndexes.includes(i);
    return false;
  };

  useEffect(() => {
    if (!isFade) return;
    if (!isAutoPlay) return;
    if (components.length <= 1) return;

    const offset = pausedElapsedRef.current || 0;

    setTickStart(performance.now() - offset);

    const remaining = Math.max(0, autoPlayInterval - offset);
    const id = setTimeout(() => {
      nextSlide();
    }, remaining);

    pausedElapsedRef.current = 0;

    return () => clearTimeout(id);
  }, [currentIndex, isFade, isAutoPlay, components.length, autoPlayInterval]);

  useEffect(() => {
    if (!tickStart) return;
    if (!isAutoPlay) return;
    if (components.length <= 1) return;

    let raf;
    const loop = (now) => {
      const elapsed = now - tickStart;
      const p = Math.max(0, Math.min(1, elapsed / autoPlayInterval));
      setProgress(p);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [tickStart, isAutoPlay, components.length, autoPlayInterval]);

  useEffect(() => {
    if (currentIndex !== prevIndexRef.current) {
      if (isAutoPlay) bumpTimer();
      prevIndexRef.current = currentIndex;
    }
  }, [currentIndex, isAutoPlay]);

  const hasMultipleSlides = Array.isArray(components) && components.length > 1;
  useEffect(() => {
    if (pauseCarrousel === undefined) return;
    setIsAutoPlay(!pauseCarrousel);
  }, [pauseCarrousel]);
  return (
    <Fragment>
      <div
        className={`${styles['carousel-container']} ${className ?? ''}`}
        style={style}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={`${styles['carousel']} ${containerClass ? containerClass : ''}`}
        >
          <div
            className={`${styles['carousel-track']} ${isFade ? styles['fade-mode'] : ''}`}
            style={
              isFade
                ? {
                    ['--fade-duration']: `${fadeDuration}ms`,
                    ['--fade-ease']: fadeEasing,
                  }
                : {
                    transform: `translateX(-${currentIndex * 100}%)`,
                    transition: 'transform 0.5s ease-in-out',
                  }
            }
          >
            {components.map((Component, index) => (
              <div
                key={index}
                className={`${styles['carousel-slide']} ${slideClassName ?? ''} ${
                  index === currentIndex ? styles.active : ''
                } ${isFade ? styles['fade-slide'] : ''}`}
              >
                {Component}
              </div>
            ))}
          </div>

          {hasArrows && components.length > 1 && (
            <>
              <button
                className={`${styles['carousel-arrow']} ${styles['prev']} ${nextStyle}`}
                onClick={prevSlide}
              >
                <img src={rightArrow} alt="Previous slide" />
              </button>
              <button
                className={`${styles['carousel-arrow']} ${styles['next']} ${prevStyle}`}
                onClick={nextSlide}
              >
                <img src={leftArrow} alt="Next slide" />
              </button>
            </>
          )}
        </div>

        {hasDots && components.length > 1 && (
          <>
            {!isArrowed ? (
              <div className={`${styles['carousel-dots']} ${dots}`}>
                {components.slice(startDot, endDot).map((_, index) => {
                  const actualIndex = startDot + index;
                  return (
                    <span
                      key={actualIndex}
                      className={`${styles['dot']} ${actualIndex === currentIndex ? styles.active : ''}`}
                      onClick={() => {
                        setCurrentIndex(actualIndex);
                        if (isAutoPlay) bumpTimer();
                      }}
                      style={{
                        backgroundColor:
                          activeDotClass && actualIndex === currentIndex
                            ? '#008755'
                            : '',
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              <div
                className={`${styles['carousel-dots']} ${styles['with-arrows']} ${dots}`}
              >
                <img
                  src={arrowLeft}
                  alt="Previous"
                  className={`${styles['arrow']} ${isBlackArrow ? styles['black'] : ''}`}
                  onClick={prevSlide}
                />

                {showAutoPlayControl && hasMultipleSlides && !isMobile && (
                  <div
                    className={`${styles['auto-dot']} ${isAutoPlay ? styles.active : ''}`}
                    aria-label={
                      isAutoPlay ? 'Pause autoplay' : 'Start autoplay'
                    }
                    title={isAutoPlay ? 'Pause autoplay' : 'Start autoplay'}
                    onClick={() => {
                      if (onPauseClick) onPauseClick((prev) => !prev);

                      setIsAutoPlay((s) => {
                        const next = !s;
                        if (!next) {
                          pausedElapsedRef.current =
                            progress * autoPlayInterval;
                        }
                        return next;
                      });
                    }}
                    style={{ ['--progress']: `${progress}` }}
                  >
                    {isAutoPlay ? (
                      <img
                        className={styles['control-image']}
                        src={pauseSvg}
                        alt="Play"
                      />
                    ) : (
                      <img
                        className={styles['control-image']}
                        src={playSvg}
                        alt="Pause"
                        style={{ marginLeft: '2px' }}
                      />
                    )}
                  </div>
                )}

                {components.slice(startDot, endDot).map((_, index) => {
                  const actualIndex = startDot + index;
                  const isVideoDot = isVideoIndex(actualIndex);
                  const isActive = actualIndex === currentIndex;
                  return isVideoDot ? (
                    <img
                      key={actualIndex}
                      src={carouselPause}
                      alt="Video slide"
                      className={`${styles['dot-video']}`}
                      onClick={() => {
                        setCurrentIndex(actualIndex);
                        if (isAutoPlay) bumpTimer();
                      }}
                    />
                  ) : (
                    <span
                      key={actualIndex}
                      className={`${styles['dot']} ${isActive ? styles.active : ''}`}
                      onClick={() => {
                        setCurrentIndex(actualIndex);
                        if (isAutoPlay) bumpTimer();
                      }}
                      style={{
                        backgroundColor:
                          activeDotClass && isActive ? '#008755' : '',
                      }}
                    />
                  );
                })}

                <img
                  src={arrowRight}
                  alt="Next"
                  className={`${styles['arrow']} ${isBlackArrow ? styles['black'] : ''}`}
                  onClick={nextSlide}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Fragment>
  );
};

export default Carousel;
